import { sql } from 'drizzle-orm'
import {
  boolean,
  index,
  pgEnum,
  pgTable,
  text,
  uniqueIndex,
} from 'drizzle-orm/pg-core'
import { phoneNumberColumn } from '../common/customTypes'
import {
  getBaseFields,
  getBaseIndexes,
  getTimestampWithTimezone,
  typedJsonb,
} from '../common/utils'

/**
 * Per-user Telnyx Telephony Credential for inbound call routing.
 * Presence of this column = user is in the inbound pool.
 * NULL = not opted in (no credential provisioned).
 */
export interface TelnyxInbound {
  credentialId: string
  sipUsername: string
}

// Role enum
export const roleEnum = pgEnum('role', [
  'user',
  'officer',
  'admin',
  'employee',
  'service_manager',
])

// User status enum - for account access control only
export const userStatusEnum = pgEnum('user_status', [
  'active', // Can login and use the system
  'inactive', // Account disabled/blocked
])

// UI Language enum
export const uiLanguageEnum = pgEnum('ui_language', ['en', 'fi', 'es'])

// Users table
export const users = pgTable(
  'users',
  {
    ...getBaseFields(),
    email: text(), // Required for login. Phone-only users are lead/contact records (cannot log in).
    firstName: text().notNull().default(''),
    lastName: text().notNull().default(''),
    phoneNumber: phoneNumberColumn(),
    googleId: text(), // Google OAuth user ID
    roles: roleEnum('roles').array().notNull().default(['user']),
    status: userStatusEnum().notNull().default('active'),
    emailVerifiedAt: getTimestampWithTimezone(),
    marketingConsentAt: getTimestampWithTimezone(),
    uiLanguage: uiLanguageEnum().notNull().default('fi'),
    // Write-once, set at user creation from the client-provided attribution
    // cookie's firstTouch. The canonical source of truth for "how did we
    // originally get this person" — customers.internalFirstTouchAcquisition
    // is a cached copy of this value for the customer's owning user.
    // `internal` prefix keeps it out of responses served to non-staff roles.
    // See docs/attribution-semantics.md.
    internalFirstTouchAcquisition: typedJsonb(
      'internal_first_touch_acquisition',
    ),
    telnyxInbound: typedJsonb<TelnyxInbound>('telnyx_inbound'),
    telnyxOutboundEnabled: boolean('telnyx_outbound_enabled')
      .notNull()
      .default(false),
    // Non-null = user asked not to be called. Used by the calling-queue
    // dedup to exclude them globally across all campaigns.
    doNotCallAt: getTimestampWithTimezone(),
    emailSignature: text(),
  },
  (table) => ({
    ...getBaseIndexes('users', table),
    emailUniqueIdx: uniqueIndex('users_email_unique_idx').on(
      sql`lower(${table.email})`,
    ),
    googleIdUniqueIdx: uniqueIndex('users_google_id_unique_idx').on(
      table.googleId,
    ),
    phoneNumberIdx: index('users_phone_number_idx').on(table.phoneNumber),
  }),
)
